/**
 * AIDIA AI layer.
 *
 * Two routes, chosen in Settings:
 *   • backend  — the server holds the Anthropic key; the browser holds none.
 *   • direct   — the browser calls Anthropic with the user's own key, which is
 *                stored encrypted in the vault like any other secret.
 *
 * In both cases the note text being worked on is decrypted first and sent to
 * Anthropic. Stored password values are stripped before anything is sent.
 */

import * as api from "./api.js";

const DIRECT_URL = "https://api.anthropic.com/v1/messages";
const API_VERSION = "2023-06-01";

export const MODELS = [
  { id: "claude-opus-5", label: "Claude Opus 5", note: "Most capable" },
  { id: "claude-sonnet-5", label: "Claude Sonnet 5", note: "Balanced" },
  { id: "claude-haiku-4-5", label: "Claude Haiku 4.5", note: "Fastest & cheapest" },
];
export const DEFAULT_MODEL = "claude-opus-5";

/**
 * One request. `settings` comes from the vault: { useBackend, apiKey, model }.
 * `effort` trades thoroughness for cost — the short editor assists use "low".
 */
async function ask(settings, { system, prompt, maxTokens = 16000, effort }) {
  const model = settings.model || DEFAULT_MODEL;
  const messages = [{ role: "user", content: prompt }];

  if (settings.useBackend) {
    const res = await api.aiMessage({ model, system, messages, maxTokens, effort });
    return res.text;
  }

  if (!settings.apiKey) {
    throw new Error("No Anthropic API key set. Add one in Settings, or switch on the backend route.");
  }

  const res = await fetch(DIRECT_URL, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-api-key": settings.apiKey,
      "anthropic-version": API_VERSION,
      "anthropic-dangerous-direct-browser-access": "true",
    },
    body: JSON.stringify({
      model,
      max_tokens: maxTokens,
      ...(system ? { system } : {}),
      ...(effort ? { output_config: { effort } } : {}),
      messages,
    }),
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error?.message || `Anthropic returned ${res.status}`);
  if (body.stop_reason === "refusal") throw new Error("The model declined this request.");

  return (body.content || [])
    .filter((b) => b.type === "text")
    .map((b) => b.text)
    .join("")
    .trim();
}

export const isConfigured = (settings) =>
  Boolean(settings?.useBackend || settings?.apiKey);

// --- in-editor assists ----------------------------------------------------

export const summarize = (settings, text) =>
  ask(settings, {
    system: "You summarise personal diary and journal entries. Reply with 2–3 sentences of plain prose. No preamble, no bullet points.",
    prompt: `Summarise this entry:\n\n${text}`,
    maxTokens: 1000,
    effort: "low",
  });

export const suggestTags = (settings, text) =>
  ask(settings, {
    system: "You tag personal notes. Reply with 3–6 short lowercase tags, comma separated. Output only the tags.",
    prompt: `Suggest tags for this entry:\n\n${text}`,
    maxTokens: 200,
    effort: "low",
  });

export const suggestTitle = (settings, text) =>
  ask(settings, {
    system: "You title personal notes. Reply with one short title under 8 words. Output only the title, with no quotes.",
    prompt: `Suggest a title for this entry:\n\n${text}`,
    maxTokens: 100,
    effort: "low",
  });

// --- ask across the vault -------------------------------------------------

/**
 * Build the context block. Password values are removed here — this is the only
 * place note content is assembled for sending, so it is the right place to
 * enforce that the vault's secrets never leave.
 */
export function buildContext(entries, limit = 60) {
  const usable = entries
    .slice()
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
    .slice(0, limit);

  const blocks = usable.map((e, i) => {
    const parts = [
      `[Entry ${i + 1}]`,
      `category: ${e.category}`,
      e.title ? `title: ${e.title}` : null,
      e.tags?.length ? `tags: ${e.tags.join(", ")}` : null,
      e.dueDate ? `due: ${e.dueDate}` : null,
      e.createdAt ? `written: ${new Date(e.createdAt).toISOString().slice(0, 10)}` : null,
      // Password entries contribute their label and username only.
      e.category === "password"
        ? `username: ${e.username || "—"}${e.url ? `, site: ${e.url}` : ""} (password value withheld)`
        : `content: ${(e.content || "").slice(0, 1200)}`,
    ].filter(Boolean);
    return parts.join("\n");
  });

  return { text: blocks.join("\n\n---\n\n"), used: usable };
}

export async function askVault(settings, question, entries) {
  const { text, used } = buildContext(entries);
  if (!used.length) throw new Error("There are no entries to search yet.");

  const answer = await ask(settings, {
    system:
      "You answer questions about the user's own private diary, journal, notes and reminders. " +
      "Use only the supplied entries. Cite the entries you used as [Entry N]. " +
      "If the entries do not answer the question, say so plainly. " +
      "Stored password values are deliberately withheld from you — never claim to know one.",
    prompt: `Here are the user's entries:\n\n${text}\n\n---\n\nQuestion: ${question}`,
    maxTokens: 4000,
    effort: "medium",
  });

  return { answer, used };
}
